/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { useState, useRef, forwardRef } from 'react'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import { addDays } from 'date-fns'
import { enGB, enUS } from 'date-fns/locale'
import { TextField } from '@alto-avios/alto-ui'
import MobileModal from '../MobileModal'
import Calendar from './Calendar'
import { useClickAway } from '../../hooks/useClickAway'
import { useLiftMediaQuery } from '../../hooks/useLiftMediaQuery'
import { deviceSizes } from '../../theme/deviceSizes'
import styles from './DatePicker.module.scss'
import { DatePickerProps } from './DatePicker.types'

const enGBLocaleCode = 'en-GB'
const enUSLocaleCode = 'en-US'

registerLocale(enGBLocaleCode, enGB)
registerLocale(enUSLocaleCode, enUS)
setDefaultLocale(enGBLocaleCode)

const DEFAULT_LOCALE_CODE = enGBLocaleCode

const getLocaleObjectByLocaleCode = (localeCode: string) => {
  if (localeCode === enUSLocaleCode) {
    return enUS
  }

  return enGB
}

export default forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    isRangePicker,
    startLabel = 'Start',
    endLabel = 'End',
    minDate = new Date(),
    maxDate = addDays(new Date(), 365),
    values = {
      startDateTime: null,
      endDateTime: null,
    },
    onChange = () => null,
    singleDay = false,
    allowSameDay = false,
    maxEndDate,
    locale = DEFAULT_LOCALE_CODE,
    endDateSelectable = false,
    errorStartDate,
    errorEndDate,
    disabled,
    ...calendarProps
  },
  ref
) {
  const [showCalendar, setShowCalendar] = useState(false)
  const [showEndCalendar, setShowEndCalendar] = useState(false)
  const isTabletOrSmaller = useLiftMediaQuery(`(max-width: ${deviceSizes.medium})`)

  const calendarRef = useRef<HTMLInputElement>(null)
  const startDateWrapperRef = useRef<HTMLDivElement>(null)
  const endDateWrapperRef = useRef<HTMLDivElement>(null)

  const localeObject = getLocaleObjectByLocaleCode(locale)

  useClickAway(calendarRef, !isTabletOrSmaller && showCalendar, () => {
    setShowCalendar(false)
    setShowEndCalendar(false)
  })

  const onChangeCalendar = (dates: Date | [Date, Date], event: React.SyntheticEvent<any>) => {
    if (!isRangePicker) {
      onChange(dates, event)
      setShowCalendar(false)
      return
    }

    if (!dates) {
      return
    }

    if (endDateSelectable && !Array.isArray(dates) && !allowSameDay) {
      onChange([null, dates], event)
      setShowCalendar(false)
      return
    }

    const [start, end] = singleDay ? [dates as Date, undefined] : (dates as [Date, Date])

    if (!allowSameDay && end?.getTime() === start.getTime()) {
      end.setDate(start.getDate() + 1)
    }

    onChange([start, end ?? null], event)

    if (singleDay || end != null) {
      setShowCalendar(false)
    }
  }

  const selectingEndDate =
    !singleDay &&
    isRangePicker &&
    endDateSelectable &&
    showEndCalendar &&
    values.startDateTime !== undefined

  const onClickStartField = () => {
    const input = startDateWrapperRef.current?.querySelector('input')
    input?.focus()
    setShowCalendar(!showCalendar)
    setShowEndCalendar(false)
    setTimeout(() => calendarRef.current?.focus(), 100)
  }
  const onClickEndField = () => {
    if (singleDay) {
      return
    }
    const input = endDateWrapperRef.current?.querySelector('input')
    input?.focus()
    setShowCalendar(!showCalendar)
    setShowEndCalendar(endDateSelectable && !!values.startDateTime)
    setTimeout(() => calendarRef.current?.focus(), 100)
  }

  let calendarMinDate = minDate
  let calendarMaxDate = maxDate
  if (isRangePicker && !singleDay && selectingEndDate && !!values.startDateTime) {
    calendarMinDate = addDays(values.startDateTime, 1)
  }
  if (isRangePicker && !singleDay && maxEndDate && (selectingEndDate || !values.endDateTime)) {
    calendarMaxDate = maxEndDate
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    const element = event.target as HTMLElement
    if (element.tagName === 'INPUT') {
      return
    }
    if (startDateWrapperRef.current?.contains(element)) {
      onClickStartField()
    }
    if (endDateWrapperRef.current?.contains(element)) {
      onClickEndField()
    }
  }

  return (
    <div className={styles.equallySpacedDiv} ref={calendarRef} onClick={handleClick}>
      <div className={styles.placeContentEnd} ref={startDateWrapperRef}>
        <TextField
          name={startLabel}
          label={startLabel}
          isReadOnly
          isRequired
          isDisabled={disabled}
          value={values.startDateTime?.toLocaleDateString(locale) || ''}
          onFocus={onClickStartField}
          iconEndProps={{
            iconName: 'calendar',
            color: showCalendar ? 'accentPrimary' : '',
          }}
          errorMessage={errorStartDate}
          isInvalid={!!errorStartDate}
        />
      </div>

      {showCalendar && (
        <div className={styles.calendarWrapper}>
          <MobileModal
            id="datePickerRange"
            isModalVisible={showCalendar && isTabletOrSmaller}
            handleModalClose={() => {
              setShowCalendar(false)
              setShowEndCalendar(false)
            }}
          >
            <Calendar
              tabIndex={-1}
              monthsShown={isRangePicker && !singleDay ? 2 : 1}
              selected={values.startDateTime}
              onChange={onChangeCalendar}
              minDate={calendarMinDate}
              maxDate={calendarMaxDate}
              selectsRange={isRangePicker && !singleDay && !selectingEndDate}
              selectsEnd={selectingEndDate}
              startDate={values.startDateTime}
              endDate={values.endDateTime}
              locale={localeObject}
              {...calendarProps}
            />
          </MobileModal>
        </div>
      )}

      {isRangePicker ? (
        <div ref={endDateWrapperRef}>
          <TextField
            name={endLabel}
            label={endLabel}
            isReadOnly
            isRequired
            value={values.endDateTime?.toLocaleDateString(locale) || ''}
            onFocus={onClickEndField}
            iconEndProps={{
              iconName: 'calendar',
              color: showCalendar || showEndCalendar ? 'accentPrimary' : '',
            }}
            isDisabled={singleDay}
            errorMessage={errorEndDate}
            isInvalid={!!errorEndDate}
          />
        </div>
      ) : null}
    </div>
  )
})
